import {
	Box,
	Typography,
	Alert,
	useTheme,
	Collapse,
	IconButton,
	CircularProgress,
	LinearProgress,
	Button,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAppSelector, useAppDispatch } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useTelematicsFeature } from '@acorn-portal/shared/src/hooks/useTelematicsFeature';
import { useEffect, useState, useMemo, FC } from 'react';
import { useNavigate } from 'react-router';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { fetchMileageSummary } from '@acorn-portal/shared/src/store/telematicsDriverMileageSlice';
import { fetchDriverMileage } from '@acorn-portal/shared/src/store/driverMileageSlice';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import MileageErrorPanel from './mileage-error-panel';
import {
	formatPolicyEndDate,
	calculateMileageStats,
	getProgressColor,
	getWarningTitle,
	getWarningMessage,
} from './mileage-utils';

/**
 * Props for the MileageTracker component.
 */
interface MileageTrackerProps {
	/** Override annual mileage with a proposed value (e.g., during top-up flow). */
	proposedMileage?: number;
	/** Hide the low mileage / exceeded warning alert. Default: false */
	hideWarning?: boolean;
	/** Hide the "Top Up Miles" button. Default: false */
	hideTopUpButton?: boolean;
	/** Hide the title header (useful when parent provides DashboardCardHeading). Default: false */
	hideTitle?: boolean;
}

/**
 * Main mileage tracking dashboard component with progress visualization.
 *
 * Displays the driver's mileage usage against their annual allowance with a
 * progress bar, remaining miles, and optional warning alerts. Supports both
 * telematics-enabled policies (with live data from API) and standard policies
 * (showing static policy data).
 *
 * Features:
 * - Progress bar with color-coded status (green/amber/red)
 * - Warning alerts when approaching or exceeding limits
 * - Info modal with detailed mileage breakdown
 * - Navigation to mileage top-up flow
 * - Backwards compatible with non-telematics policies
 *
 * @component
 * @example
 * ```tsx
 * // Standard usage on policy details page
 * <MileageTracker />
 *
 * // During mileage top-up flow with proposed value
 * <MileageTracker proposedMileage={12000} hideTopUpButton />
 *
 * // Read-only view without warnings or button
 * <MileageTracker hideWarning hideTopUpButton />
 * ```
 */
export const MileageTracker: FC<MileageTrackerProps> = ({
	proposedMileage,
	hideWarning = false,
	hideTopUpButton = false,
	hideTitle = false,
}) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	// Telematics feature flag - controls whether API calls are made
	const isTelematicsFeatureEnabled = useTelematicsFeature();

	// Get policy details from Redux
	const policy = useAppSelector((x) => x.fullPolicy);
	const { policyToken, policyNumber, endDate: policyEndDate, vehicleDetails } = policy ?? {};

	// Get telematics enabled status from policy check
	const { policyExists } = useAppSelector((state) => state.telematicsEnabledPolicy);
	const isTelematicsPolicyEnabled = policyExists?.exists ?? false;

	// Combined check: feature flag must be on AND policy must be telematics-enabled
	const isTelematicsEnabled = isTelematicsFeatureEnabled && isTelematicsPolicyEnabled;

	// Get mileage summary from Redux (telematics API)
	const { summary, summaryLoading, summaryError } = useAppSelector((state) => state.telematicsDriverMileage);

	// Get driver mileage from Redux (non-telematics fallback)
	const driverMileageState = useAppSelector((state) => state.driverMileage);

	const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);

	const handleOpenInfoModal = () => setInfoModalOpen(true);
	const handleCloseInfoModal = () => setInfoModalOpen(false);

	const handleTopUpMiles = () => {
		navigate(ROUTE_PATHS.topUpMiles);
	};

	// Fetch mileage summary when policy number is available and telematics is enabled (feature flag + policy check)
	useEffect(() => {
		if (isTelematicsFeatureEnabled && policyNumber && isTelematicsPolicyEnabled) {
			dispatch(fetchMileageSummary({ policyReference: policyNumber }));
		}
	}, [policyNumber, isTelematicsPolicyEnabled, isTelematicsFeatureEnabled, dispatch]);

	// Fetch driver mileage from the Driver Mileage API when telematics is not enabled
	useEffect(() => {
		if (!isTelematicsEnabled && policyToken) {
			dispatch(fetchDriverMileage({ policyToken }));
		}
	}, [isTelematicsEnabled, policyToken, dispatch]);

	// Determine data source based on telematics enabled status
	const resolvedAnnualMileage =
		proposedMileage ?? (isTelematicsEnabled ? summary?.annualMileageAllowance : vehicleDetails?.annualMileage);
	const annualMileage = resolvedAnnualMileage ?? 0;
	const driverMileage = isTelematicsEnabled ? summary?.milesDrivenThisTerm : driverMileageState.mileage?.actualMiles;
	const endDateSource = isTelematicsEnabled ? summary?.termEndDate : policyEndDate;

	// Format the policy end date using utility function
	const formattedEndDate = useMemo(() => formatPolicyEndDate(endDateSource), [endDateSource]);

	// Calculate mileage stats using utility function
	const { actualMileage, milesRemaining, usedPercentage, isExceeded, isLowOnMiles } = useMemo(
		() => calculateMileageStats(driverMileage, annualMileage, isTelematicsEnabled, summary?.exceedsAllowanceWarning),
		[driverMileage, annualMileage, isTelematicsEnabled, summary?.exceedsAllowanceWarning],
	);

	const isUsingProposedMileage = proposedMileage !== undefined;

	// Get warning content using utility functions
	const warningTitle = getWarningTitle(isExceeded, isUsingProposedMileage);
	const warningMessage = getWarningMessage(isExceeded, isUsingProposedMileage);
	const progressColor = getProgressColor(isExceeded, usedPercentage, theme);

	const isLoading = isTelematicsEnabled ? summaryLoading : driverMileageState.loading;
	const hasError = isTelematicsEnabled
		? summaryError || driverMileage === undefined
		: driverMileageState.error !== null;
	const missingAnnualMileage = resolvedAnnualMileage == null;

	if (isLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '200px',
				}}
			>
				<CircularProgress color='primary' />
			</Box>
		);
	}

	if (hasError || missingAnnualMileage) {
		return <MileageErrorPanel annualMileage={annualMileage} />;
	}

	return (
		<Box sx={{ position: 'relative' }}>
			{/* Header with title and info icon */}
			{!hideTitle && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						mb: 2,
					}}
				>
					<Typography
						variant='h5'
						sx={{
							fontWeight: 500,
							color: theme.palette.secondary.main,
						}}
					>
						Annual Mileage Tracker
					</Typography>
					<IconButton size='small' onClick={handleOpenInfoModal} sx={{ p: 0 }} aria-label='Mileage tracker information'>
						<InfoIcon
							sx={{
								fontSize: 22,
								color: theme.palette.success.main,
							}}
						/>
					</IconButton>
				</Box>
			)}

			{/* Miles remaining text */}
			<Typography
				variant='body1'
				sx={{
					mb: 2,
					color: theme.palette.text.primary,
				}}
			>
				You have{' '}
				<Typography
					component='span'
					sx={{
						fontWeight: 700,
						color: isExceeded ? theme.palette.error.main : theme.palette.text.primary,
					}}
				>
					{milesRemaining.toLocaleString()} miles
				</Typography>{' '}
				left{formattedEndDate ? ` until ${formattedEndDate}` : ''}
			</Typography>

			{/* Progress bar */}
			<Box sx={{ mb: 1 }}>
				<LinearProgress
					variant='determinate'
					value={Math.min(100, usedPercentage)}
					sx={{
						height: 12,
						borderRadius: 6,
						backgroundColor: theme.palette.grey[200],
						'& .MuiLinearProgress-bar': {
							backgroundColor: progressColor,
							borderRadius: 6,
						},
					}}
				/>
			</Box>

			{/* Stats row */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					mb: 2,
				}}
			>
				<Typography
					variant='body2'
					sx={{
						fontWeight: 600,
						color: theme.palette.text.secondary,
					}}
				>
					Used: {actualMileage.toLocaleString()} miles
				</Typography>
				<Typography
					variant='body2'
					sx={{
						fontWeight: 600,
						color: theme.palette.text.secondary,
					}}
				>
					Limit: {annualMileage.toLocaleString()} miles
				</Typography>
			</Box>

			{/* Warning/Error Alert */}
			<Collapse in={(isExceeded || isLowOnMiles) && !hideWarning} timeout={300}>
				<Alert
					severity='error'
					icon={<ErrorOutlineIcon />}
					sx={{
						mb: 2,
						backgroundColor: isExceeded ? `${theme.palette.error.main}10` : `${theme.palette.warning.main}10`,
						border: `1px solid ${isExceeded ? theme.palette.error.main : theme.palette.warning.main}30`,
						'& .MuiAlert-icon': {
							color: isExceeded ? theme.palette.error.main : theme.palette.warning.main,
						},
					}}
				>
					<Typography
						variant='body2'
						sx={{
							fontWeight: 600,
							color: isExceeded ? theme.palette.error.dark : theme.palette.warning.dark,
							mb: 0.5,
						}}
					>
						{warningTitle}
					</Typography>
					<Typography
						variant='body2'
						sx={{
							color: theme.palette.text.secondary,
						}}
					>
						{warningMessage}
					</Typography>
				</Alert>
			</Collapse>

			{/* Top Up Miles Button */}
			{!hideTopUpButton && (
				<Button
					variant='outlined'
					color='warning'
					fullWidth
					onClick={handleTopUpMiles}
					sx={{
						textTransform: 'none',
						fontWeight: 600,
						py: 1.5,
						borderRadius: 1,
						borderColor: theme.palette.warning.main,
						color: theme.palette.warning.dark,
						'&:hover': {
							borderColor: theme.palette.warning.dark,
							backgroundColor: `${theme.palette.warning.main}08`,
						},
					}}
				>
					Top Up Miles
				</Button>
			)}

			{/* Info Modal */}
			<HelperModal
				open={infoModalOpen}
				onClose={handleCloseInfoModal}
				title='Annual Mileage Tracker'
				description={`Your mileage tracker shows how many miles you have remaining on your policy until its end date.<br/><br/>
					<strong>Progress Bar Colours:</strong><br/>
					<span style="color: #2e7d32;">●</span> <strong>Green:</strong> You're well within your mileage limit<br/>
					<span style="color: #ed6c02;">●</span> <strong>Amber:</strong> You're approaching your mileage limit<br/>
					<span style="color: #d32f2f;">●</span> <strong>Red:</strong> You've exceeded or are very close to your limit<br/><br/>
					If you need more miles, you can top up your allowance using the "Top Up Miles" button.`}
				closeText='Got it'
			/>
		</Box>
	);
};

export default MileageTracker;
